/**
 * @sazeo/sdk — CRM Contact Deduplication hooks
 *
 * TanStack Query wrappers for contact deduplication operations.
 * All calls route through the Edge Function at `/app/crm/dedup/*`
 * and enforce RLS via the caller's JWT.
 *
 * Paired spec: specs/CODEX_TASK_L91_CONTACT_DEDUPLICATION.md
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface DuplicatePair {
  contact_id_1: string;
  contact_id_2: string;
  similarity_score: number;
  reason: 'email_match' | 'name_match' | 'phone_match' | 'combined';
}

export interface MergeHistoryEntry {
  id: string;
  primary_id: string;
  merged_id: string;
  merged_at: string;
  unmerged_at: string | null;
  merged_by: string;
  merged_snapshot: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const dedupKeys = {
  all: ['crm-dedup'] as const,
  scan: (threshold?: number) => [...dedupKeys.all, 'scan', threshold ?? 0.8] as const,
  history: (page?: number) => [...dedupKeys.all, 'history', page ?? 1] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (mirror sdk/addons.ts response handling)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/**
 * Scan for duplicate contacts
 */
export function useFindDuplicates(supabase: SupabaseClientLike, threshold = 0.8) {
  return useMutation({
    mutationFn: async () => {
      return apiFetch<{ pairs: DuplicatePair[]; count: number }>(supabase, 'app/crm/dedup/scan', {
        method: 'POST',
        body: JSON.stringify({ threshold }),
      });
    },
  });
}

/**
 * Merge two contacts
 */
export function useMergeContacts(supabase: SupabaseClientLike) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { primary_id: string; merged_id: string }) =>
      apiFetch<{ primary_id: string; merged_id: string; merged_at: string }>(
        supabase,
        'app/crm/dedup/merge',
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dedupKeys.all });
    },
  });
}

/**
 * Unmerge contacts (restore within 7 days)
 */
export function useUnmergeContact(supabase: SupabaseClientLike) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mergeRecordId: string) =>
      apiFetch<{ unmerged_at: string }>(supabase, 'app/crm/dedup/unmerge', {
        method: 'POST',
        body: JSON.stringify({ merge_record_id: mergeRecordId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dedupKeys.all });
    },
  });
}

/**
 * Fetch merge history with pagination
 */
export function useMergeHistory(supabase: SupabaseClientLike, page = 1, size = 50) {
  return useQuery({
    queryKey: dedupKeys.history(page),
    queryFn: async () =>
      apiFetch<{ merges: MergeHistoryEntry[]; total: number; page: number; size: number }>(
        supabase,
        `app/crm/dedup/history?page=${page}&size=${size}`,
      ),
  });
}

export default {
  useFindDuplicates,
  useMergeContacts,
  useUnmergeContact,
  useMergeHistory,
};
